import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { ActionCommand, RygenMenuItem } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';
import { Order as EsOrder } from '@rygen/tms-search-model/search/order';

import { OrderAction, OrderSearchData, OrderStatus } from '@/model';

interface userOrderBatchActionResponse {
  availableActions: ComputedRef<RygenMenuItem[]>;
  allHaveSameStatus: ComputedRef<boolean>;
  status: ComputedRef<OrderStatus | undefined>;
  actions: ComputedRef<{ [orderAction in OrderAction]: RygenMenuItem }>;
  actionsByStatus: ComputedRef<{
    [orderStatus in OrderStatus]: RygenMenuItem[];
  }>;
}

export const useOrderBatchAction = (
  selectedData: MaybeRefOrGetter<OrderSearchData[] | EsOrder[]>,
  emitCommand: (payload: ActionCommand<OrderAction>) => void,
): userOrderBatchActionResponse => {
  const isRouteBuilderEnabled = useFlag('route-builder-feat-flag');

  const { t } = useI18n();

  const allHaveSameStatus = computed(() => {
    const orders = toValue(selectedData);
    return orders.every((o, _i, self) => o.status === self[0].status);
  });

  const allHaveSameOwner = computed(() => {
    const orders = toValue(selectedData);
    return orders.every(
      (o, _i, self) => o.owner?.naturalId === self[0].owner?.naturalId,
    );
  });

  const status = computed(() => {
    const orders = toValue(selectedData);
    if (!orders?.length || !allHaveSameStatus.value) {
      return;
    }

    return orders[0].status as OrderStatus;
  });

  const selectedOrderIds = computed(() => {
    const orders = toValue(selectedData);
    return orders.map((row) => row.id).filter(notNull);
  });

  const haveUnplannedShipOrders = computed(() => {
    const orders = toValue(selectedData);
    return orders.every((o) => o.hasAnyUnplannedShipOrders);
  });

  const createEmptyAction = (action: OrderAction) => ({
    key: action,
    label: '',
    command: () => emitCommand({ action }),
    visible: false,
  });

  const actions: ComputedRef<{ [orderAction in OrderAction]: RygenMenuItem }> =
    computed(() => {
      const orders = toValue(selectedData);

      return {
        [OrderAction.ADD_TAGS]: {
          key: OrderAction.ADD_TAGS,
          label: t('common.addParam', { param: t('tags.tags', 2) }),
          icon: PrimeIcons.TAG,
          command: () => emitCommand({ action: OrderAction.ADD_TAGS }),
        },
        [OrderAction.CANCEL]: {
          key: OrderAction.CANCEL,
          label: t('common.cancelParam', {
            param: t('orders.orders', orders.length),
          }),
          icon: PrimeIcons.TIMES,
          command: () => emitCommand({ action: OrderAction.CANCEL }),
          visible: allHaveSameStatus.value,
        },
        [OrderAction.CLONE]: {
          key: OrderAction.CLONE,
          label: t('common.duplicateParam', {
            param: t('orders.orders', orders.length),
          }),
          icon: PrimeIcons.COPY,
          command: () => emitCommand({ action: OrderAction.CLONE }),
          requiredAuthorization: ['order', 'create'],
        },
        [OrderAction.ASSIGN_USERS]: {
          key: OrderAction.ASSIGN_USERS,
          label: t('common.assignParam', { param: t('users.users', 2) }),
          icon: PrimeIcons.USERS,
          command: () => emitCommand({ action: OrderAction.ASSIGN_USERS }),
          requiredAuthorization: ['order', 'update'],
          visible: allHaveSameOwner.value,
        },
        [OrderAction.CONSOLIDATE]: createEmptyAction(OrderAction.CONSOLIDATE),
        [OrderAction.REQUEST_SPOT_MARKET_BID]: {
          key: OrderAction.REQUEST_SPOT_MARKET_BID,
          label: t('carrierPortal.spotMarket.requestBid'),
          icon: PrimeIcons.DOLLAR,
          command: () =>
            emitCommand({ action: OrderAction.REQUEST_SPOT_MARKET_BID }),
        },
        [OrderAction.SEND_TO_ROUTE_BUILDER]: {
          key: OrderAction.SEND_TO_ROUTE_BUILDER,
          label: t('routeBuilder.sendToRouteBuilder'),
          icon: PrimeIcons.TRUCK,
          to: {
            name: 'routeBuilder',
            query: {
              orderId: selectedOrderIds.value,
            },
          },
          requiredAuthorization: ['shipment', 'consolidate'],
          visible:
            isRouteBuilderEnabled.value &&
            allHaveSameOwner.value &&
            haveUnplannedShipOrders.value,
        },
        [OrderAction.SHARE]: createEmptyAction(OrderAction.SHARE),
      };
    });

  const actionsByStatus: ComputedRef<{
    [orderStatus in OrderStatus]: RygenMenuItem[];
  }> = computed(() => ({
    [OrderStatus.PENDING]: [
      actions.value[OrderAction.CANCEL],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.REQUEST_SPOT_MARKET_BID],
      actions.value[OrderAction.ASSIGN_USERS],
      actions.value[OrderAction.SEND_TO_ROUTE_BUILDER],
    ],
    [OrderStatus.IN_PROCESS]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
    [OrderStatus.SHIPPED]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
    [OrderStatus.DELIVERED]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
    [OrderStatus.CUSTOMER_REVIEW]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
    [OrderStatus.BILLED]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
    [OrderStatus.COMPLETE]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
    [OrderStatus.CANCELED]: [
      actions.value[OrderAction.ADD_TAGS],
      actions.value[OrderAction.CLONE],
      actions.value[OrderAction.ASSIGN_USERS],
    ],
  }));

  const availableActions = computed(() => {
    if (allHaveSameStatus.value && status.value) {
      return actionsByStatus.value[status.value];
    }

    const orders = toValue(selectedData);
    const statuses = orders.map((s) => s.status as OrderStatus);
    const allAvailableActions = statuses
      .map((status) => actionsByStatus.value[status])
      .flat()
      .filter((action) => {
        // Filter out actions that aren't available in each of the selected statuses
        return statuses
          .map((status) => actionsByStatus.value[status])
          .every((actions) => actions.includes(action));
      });
    return Array.from(new Set(allAvailableActions));
  });

  return {
    availableActions,
    allHaveSameStatus,
    status,
    actions,
    actionsByStatus,
  };
};
